import { ReactNode, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';

export const DataFoundationEvents = {
	ProvisioningScreenViewed: {
		eventType: 'screen',
		name: 'provisioningScreen',
	},
	TeamChoiceScreenViewed: {
		eventType: 'screen',
		name: 'teamChoiceScreen',
	},
	TemplateChoiceScreenViewed: {
		eventType: 'screen',
		name: 'templateChoiceScreen',
	},
	JTBDScreenViewed: {
		eventType: 'screen',
		name: 'jtbdScreen',
	},
	ProjectLandingSelectionScreenViewed: {
		eventType: 'screen',
		name: 'projectLandingSelectionScreen',
	},
	TryIntent: {
		eventType: 'track',
		action: 'interacted',
		actionSubject: 'tryIntentComponent',
	},
	ProductNavigationInitialized: {
		eventType: 'track',
		action: 'initialized',
		actionSubject: 'productNavigation',
	},
} as const;

type DataFoundationEventOptionMap = typeof DataFoundationEvents;

export type DataFoundationEventOption = keyof DataFoundationEventOptionMap;

export const getDataFoundationEvent = (dataFoundationsEventOption: DataFoundationEventOption) => {
	return DataFoundationEvents[dataFoundationsEventOption];
};

export type DataFoundationsWrapperProps = {
	dataFoundationsEventOption: DataFoundationEventOption;
	attributes?: object;
	waitForAttributes?: boolean;
	fireOncePerMount?: boolean;
	children: ReactNode;
};

export const DataFoundationsWrapper = ({
	dataFoundationsEventOption,
	attributes,
	waitForAttributes,
	fireOncePerMount,
	children,
}: DataFoundationsWrapperProps) => {
	const analyticsEvents = useAnalyticsEvents();
	const { createAnalyticsEvent } = analyticsEvents;
	const wacAnalyticsClient = useAnalyticsClient();
	const hasFiredRef = useRef(false);
	const analyticsClient = fireOncePerMount ? wacAnalyticsClient : analyticsEvents;

	useEffect(() => {
		if (!analyticsClient || waitForAttributes || (fireOncePerMount && hasFiredRef.current)) {
			return;
		}

		if (fireOncePerMount) {
			hasFiredRef.current = true;
		}
		createAnalyticsEvent({
			...getDataFoundationEvent(dataFoundationsEventOption),
			attributes,
		}).fire();
	}, [
		createAnalyticsEvent,
		dataFoundationsEventOption,
		attributes,
		analyticsClient,
		fireOncePerMount,
		waitForAttributes,
	]);

	return children;
};
